import React, { useRef } from 'react';

import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { SpectrogramPoint } from '@ledium/types';
import { EnrichedItem, ListComponentProps, RespButton, WavelengthChart } from '@ledium/ui-common';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import { Alert, Box, Divider, Typography } from '@mui/material';

import { LightChannelItem } from '../types';

export type LightChannelListItem = EnrichedItem<LightChannelItem>;

type LightChannelListProps = ListComponentProps<LightChannelListItem>;

const LightChannelList = ({ itemList, onView, onEdit, onDelete, onDuplicate, allowDuplicate, userData }: LightChannelListProps) => {
    const testingIds = useRef(new Set<string>());

    function testChannel(item: LightChannelListItem) {
        if (!testingIds.current.has(item.id)) {
            testingIds.current.add(item.id);
            lediumApi.get(`lightChannels/testDmx/${item.channel}/${item.resolution}`).then(() => {
                testingIds.current.delete(item.id);
            });
        }
    }

    if (!itemList.length) {
        return null;
    }

    return (
        <div>
            {itemList.map((item, idx) => (
                <React.Fragment key={item.id}>
                    {idx > 0 && <Divider />}
                    <Box
                        sx={{ display: 'flex', gap: '12px', padding: '12px 8px', alignItems: 'stretch' }}
                        onDoubleClick={() => (item.locked ? onView(item) : onEdit(item))}
                    >
                        {Array.isArray(item.optionChart) && item.optionChart.length > 0 && (
                            <Box sx={{ width: '200px', flexShrink: 0 }}>
                                <WavelengthChart data={item.optionChart as SpectrogramPoint[]} small={true} />
                            </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6">{item.name}</Typography>
                                    <Typography variant="body2">
                                        {i18n('light_channel_details', <b>{item.channel}</b>, <b>{item.resolution}</b>)},{' '}
                                        <b>{item.spectrogramRanges?.pfd?.sum}</b> {i18n('pfd_unit')}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexShrink: 0, flexWrap: 'wrap', gap: '4px' }}>
                                    <RespButton variant="contained" color="primary" onClick={() => onView(item)} startIcon={<VisibilityIcon />}>
                                        {i18n('view')}
                                    </RespButton>
                                    {userData?.admin && (
                                        <RespButton
                                            variant="contained"
                                            color="primary"
                                            disabled={!!item.locked}
                                            onClick={() => onEdit(item)}
                                            startIcon={<EditIcon />}
                                        >
                                            {i18n('edit')}
                                        </RespButton>
                                    )}
                                    {userData?.admin && allowDuplicate && (
                                        <RespButton variant="contained" color="primary" onClick={() => onDuplicate(item)} startIcon={<CallSplitIcon />}>
                                            {i18n('duplicate')}
                                        </RespButton>
                                    )}
                                    <RespButton variant="contained" color="primary" onClick={() => testChannel(item)} startIcon={<WbIncandescentIcon />}>
                                        {i18n('test')}
                                    </RespButton>
                                    {userData?.admin && (
                                        <RespButton
                                            variant="contained"
                                            color="secondary"
                                            disabled={(!!item.locked && !item.lockedReason) || !!item.linked}
                                            onClick={() => onDelete(item)}
                                            startIcon={<DeleteIcon />}
                                        >
                                            {i18n('delete')}
                                        </RespButton>
                                    )}
                                </Box>
                            </Box>
                            {item.lockedReason && <Alert severity="warning">{item.lockedReason}</Alert>}
                            {!item.lockedReason && item.linkedReason && <Alert severity="info">{item.linkedReason}</Alert>}
                        </Box>
                    </Box>
                </React.Fragment>
            ))}
        </div>
    );
};

export default LightChannelList;
